import React from 'react';
import { Star, Sparkles, BadgePercent, Tag, ChevronRight, Loader2, Wallet } from 'lucide-react';
import { CountdownText } from '../CountdownText';
import { fmtMoney, currencyCode } from '../../lib/money';

function Skeleton({ w = 'w-full', h = 'h-4' }) {
  return <div className={`${w} ${h} rounded-md bg-slate-100 animate-pulse`} />;
}

/**
 * Left-column order card: product, live price (strikethrough when a coupon is
 * on), Swiggy-style coupon entry, and the bill with "Total due today".
 * `quote` = { currency, divisor, subtotal, discount, total, tax, code, percentOff }
 * — every number comes from Stripe (or the server quote in fallback mode).
 */
export function OrderSummary({ cfg, quote, onOpenCoupons, onApplyCode, onRemoveCode, busyCode }) {
  const coupons = cfg.coupons || [];
  const best = coupons.find((c) => c.is_best) || coupons[0];
  const credit = cfg.credit && cfg.credit.active ? cfg.credit : null;
  const ready = !!quote;
  const hasDiscount = ready && quote.discount > 0;
  const creditApplied = hasDiscount && credit && (quote.code || '').toUpperCase() === credit.code;
  const fmt = (m) => fmtMoney(m, quote?.currency, quote?.divisor);
  const code = ready ? currencyCode(quote.currency) : '';

  return (
    <section data-testid="order-summary" className="rounded-3xl bg-white border border-slate-200/80 shadow-[0_18px_50px_-30px_rgba(15,23,42,0.35)] overflow-hidden">
      <div className="p-4 sm:p-7">
        <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-amber-600">{cfg.label_order || 'Your order'}</p>
        <h1 data-testid="checkout-product-name" className="font-serif text-2xl sm:text-4xl text-slate-900 leading-[1.1] sm:leading-[1.05] mt-1 sm:mt-1.5">{cfg.product_name}</h1>
        {cfg.product_tagline && <p className="hidden sm:block text-sm text-slate-500 mt-2.5 leading-relaxed" data-testid="checkout-product-tagline">{cfg.product_tagline}</p>}
        {cfg.rating_badge && (
          <div className="inline-flex items-center gap-1.5 mt-3 sm:mt-4 rounded-full bg-amber-50 border border-amber-100 px-3 py-1 text-xs font-semibold text-amber-800" data-testid="rating-badge">
            <Star size={12} className="fill-amber-400 text-amber-400" />{cfg.rating_badge}
          </div>
        )}

        {/* Price */}
        <div className="mt-4 sm:mt-6 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5" data-testid="price-block">
          {!ready ? (
            <div className="space-y-3"><Skeleton w="w-40" h="h-9" /><Skeleton w="w-64" h="h-3" /></div>
          ) : (
            <div className="flex items-end justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-baseline gap-2.5 flex-wrap">
                  {hasDiscount && (
                    <span data-testid="price-original" className="text-lg font-semibold text-slate-400 line-through decoration-rose-400/80 decoration-2">{fmt(quote.subtotal)}</span>
                  )}
                  <span data-testid="price-current" className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{fmt(quote.total)}</span>
                  <span className="text-xs font-bold text-slate-400" data-testid="price-currency">{code}</span>
                </div>
                {cfg.price_note && <p className="text-xs text-slate-500 mt-1.5">{cfg.price_note}</p>}
              </div>
              {hasDiscount && (
                <span data-testid="savings-pill" className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold px-2.5 py-1">
                  <Sparkles size={12} />{cfg.label_you_save || 'You save'} {fmt(quote.discount)}{quote.percentOff ? ` (${quote.percentOff}%)` : ''}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Coupon (hidden for the trial — no codes or credits apply) */}
        {!cfg.coupons_disabled && <div className="mt-4" data-testid="coupon-block">
          {hasDiscount ? (
            <div data-testid="applied-coupon-row" className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 ${creditApplied ? 'border-emerald-300 bg-emerald-50' : 'border-emerald-200 bg-emerald-50/70'}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0">{creditApplied ? <Wallet size={18} /> : <BadgePercent size={18} />}</div>
                <div className="min-w-0">
                  {creditApplied ? (
                    <>
                      <p className="font-extrabold text-emerald-900 truncate" data-testid="applied-credit-label">{cfg.label_credits_applied || 'Your wallet credits'} · <span className="font-mono tracking-wider" data-testid="applied-coupon-code">{quote.code}</span></p>
                      <p className="text-xs text-emerald-700">{fmt(quote.discount)} {cfg.label_credits_applied_hint || 'from your account, taken off the bill'}{credit.expires_at ? <> · <CountdownText iso={credit.expires_at} />{credit.expires_at_text ? <span data-testid="applied-credit-expires-at"> (at {credit.expires_at_text})</span> : null}</> : ''}</p>
                    </>
                  ) : (
                    <>
                      <p className="font-mono font-extrabold tracking-wider text-emerald-900 truncate" data-testid="applied-coupon-code">{quote.code}</p>
                      <p className="text-xs text-emerald-700">{cfg.label_offer_applied || 'Offer applied on the bill'}</p>
                    </>
                  )}
                </div>
              </div>
              <button type="button" onClick={onRemoveCode} data-testid="remove-coupon-btn" className="shrink-0 -mr-2 px-3 py-2.5 rounded-lg text-xs font-bold tracking-wider text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors">{cfg.label_remove || 'REMOVE'}</button>
            </div>
          ) : (
            <>
              {credit && ready && (
                <div data-testid="credit-offer-row" className="flex items-center justify-between gap-3 rounded-2xl border border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50 px-4 py-3 mb-2.5">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0"><Wallet size={18} /></div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold tracking-widest uppercase text-emerald-700">{cfg.label_credits_title || 'Your wallet credits'}</p>
                      <p className="text-sm font-semibold text-slate-900 truncate"><span data-testid="credit-offer-balance">{credit.balance}</span> {cfg.label_credits_available || 'available'}{credit.expires_at ? <span className="text-xs font-medium text-emerald-700"> · <CountdownText iso={credit.expires_at} />{credit.expires_at_text ? <span data-testid="credit-offer-expires-at"> (at {credit.expires_at_text})</span> : null}</span> : null}</p>
                      {credit.min_order_ok === false && <p className="text-[11px] text-amber-700" data-testid="credit-min-order-note">Credits apply to orders of {credit.min_order}+ only.</p>}
                    </div>
                  </div>
                  <button type="button" onClick={() => onApplyCode(credit.code)} disabled={!!busyCode || credit.min_order_ok === false} data-testid="apply-credit-btn"
                    className="shrink-0 rounded-full bg-emerald-600 text-white text-xs font-bold tracking-wider px-4 py-2 hover:bg-emerald-700 transition-colors disabled:opacity-60 min-w-[64px] flex justify-center">
                    {busyCode === credit.code ? <Loader2 size={14} className="animate-spin" /> : (cfg.label_use_credits || 'USE')}
                  </button>
                </div>
              )}
              {best && ready && !credit && (
                <div data-testid="best-offer-row" className="flex items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0"><Tag size={18} /></div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold tracking-widest uppercase text-amber-700">{cfg.label_best_offer || 'Best offer for you'}</p>
                      <p className="text-sm font-semibold text-slate-900 truncate"><span className="font-mono" data-testid="best-offer-code">{best.code}</span> · {best.headline}</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => onApplyCode(best.code)} disabled={!!busyCode} data-testid="apply-best-coupon-btn"
                    className="shrink-0 rounded-full bg-slate-900 text-white text-xs font-bold tracking-wider px-4 py-2 hover:bg-slate-700 transition-colors disabled:opacity-60 min-w-[64px] flex justify-center">
                    {busyCode === best.code ? <Loader2 size={14} className="animate-spin" /> : (cfg.label_apply || 'APPLY')}
                  </button>
                </div>
              )}
              <button type="button" onClick={onOpenCoupons} disabled={!ready} data-testid="open-coupons-btn"
                className="mt-2.5 w-full flex items-center justify-between rounded-2xl border-2 border-dashed border-slate-300 hover:border-slate-900 px-4 py-3 text-left transition-colors group disabled:opacity-50">
                <span className="flex items-center gap-3 text-sm font-semibold text-slate-800">
                  <BadgePercent size={18} className="text-slate-500 group-hover:text-slate-900 transition-colors" />{cfg.coupon_button_label || 'Apply Coupon'}
                </span>
                <ChevronRight size={16} className="text-slate-400 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </>
          )}
        </div>}

        {/* Bill — on phones the sticky pay bar already shows the total, so this only renders from `sm` up */}
        <dl className="mt-5 space-y-2 text-sm hidden sm:block" data-testid="bill-details">
          <div className="flex justify-between gap-4 text-slate-600"><dt className="truncate">{cfg.product_name}</dt><dd className="shrink-0" data-testid="bill-subtotal">{ready ? fmt(quote.subtotal) : '—'}</dd></div>
          {hasDiscount && (
            <div className="flex justify-between gap-4 text-emerald-700" data-testid="bill-discount-row"><dt>{creditApplied ? 'Your credits' : 'Coupon'} <span className="font-mono font-bold">{quote.code}</span></dt><dd className="shrink-0 font-semibold">−{fmt(quote.discount)}</dd></div>
          )}
          {ready && quote.tax > 0 && <div className="flex justify-between gap-4 text-slate-600"><dt>{cfg.label_tax || 'Tax'}</dt><dd>{fmt(quote.tax)}</dd></div>}
          <div className="flex justify-between items-baseline gap-4 border-t border-slate-200 pt-3">
            <dt className="font-bold text-slate-900">{cfg.label_total_due || 'Total due today'}</dt>
            <dd data-testid="total-due" className="text-xl font-extrabold text-slate-900 shrink-0">{ready ? fmt(quote.total) : '—'} {ready && <span className="text-xs font-bold text-slate-400">{code}</span>}</dd>
          </div>
        </dl>
      </div>
    </section>
  );
}
